/* eslint-disable indent */
import Swiper, { Navigation, Pagination, A11y, EffectFade } from 'swiper';
import { YoutubePlayer } from '2-core-elements/Video/YoutubeAPI';
import VimeoPlayer from '@vimeo/player';
import { getVideoData, VIDEO_READY_EVENT, type VideoSource, type VideoData, PLAY_BTN_CLASS } from '2-core-elements/Video/Video';
import RotateCWIcon from '../../assets/icons/rotate-cw.svg';


Swiper.use([Navigation, Pagination]);

interface VideoSliderLabel {
    playNextVideo: string;
    playFirstVideo: string;
    replayButton: string;
}
interface VideoSliderProps {
    el: HTMLElement;
    props: {
        initialOpenedIndex: number;
        label: VideoSliderLabel;
    };
}

interface VideoItemProps {
    videoItemEl: HTMLDivElement;
    buttonCardEl?: HTMLLinkElement;
    playButton: HTMLButtonElement;
}

interface VideoOverlayEl {
    overlayEl?: HTMLDivElement;
    playNextButton?: HTMLButtonElement;
    replayButton?: HTMLButtonElement;
    nextCardElement?: HTMLLinkElement | null;
}

interface VimeoVideoPlayer extends VideoItemProps, VideoOverlayEl {
    player: Vimeo;
    videoSource: 'vimeo';
}

interface YoutubeVideoPlayer extends VideoItemProps, VideoOverlayEl {
    player: YoutubePlayer;
    videoSource: 'youtube';
}

interface UploadVideoPlayer extends VideoItemProps, VideoOverlayEl {
    player: HTMLVideoElement;
    videoSource: 'upload';
}

interface UnknownVideoPlayer extends VideoItemProps, VideoOverlayEl {
    player: null;
    videoSource: VideoSource | null;
}


type VideoPlayer = VimeoVideoPlayer | YoutubeVideoPlayer | UploadVideoPlayer | UnknownVideoPlayer;

const defaultLabelValue = {
    playNextVideo: 'Play next video:',
    playFirstVideo: 'Play from beginning:',
    replayButton: 'Replay',
};

interface RenderVideoOverlayElProps {
    el: HTMLDivElement | null;
    nextCardElement: HTMLLinkElement | null;
    label: VideoSliderLabel;
    isLastItem: boolean;
    isFirstItem: boolean;
}

const renderVideoOverlayEl = ({
    el,
    nextCardElement,
    label,
    isLastItem,
    isFirstItem,
}: RenderVideoOverlayElProps): VideoOverlayEl => {
    if (!el) {
        return {};
    }

    // if only 1 item inside the slider, no need to render the next card
    const shouldRenderNextCard = !(isFirstItem && isLastItem);

    const {
        playNextVideo = defaultLabelValue.playNextVideo,
        playFirstVideo = defaultLabelValue.playFirstVideo,
        replayButton = defaultLabelValue.replayButton,
    } = label;

    const overlayElString = `<div class="video-overlay is-hidden">
        ${nextCardElement && shouldRenderNextCard ? `
        <div class="video-overlay-next">
            <p>${isLastItem ? playFirstVideo : playNextVideo}</p>
        </div>            
        ` : ''}
        <div class="video-overlay-replay-wrapper">
             <button type="button" class=" btn-skin-2-dark btn-size-sm video-ovelay-replay-button">
                ${RotateCWIcon}
                <span class="btn-text">${replayButton}</span>
             </button>
         </div>
    </div>`;

    el.insertAdjacentHTML('afterend', overlayElString);

    if (nextCardElement && shouldRenderNextCard) {
        const overlayVideoNextEl = el.nextElementSibling?.querySelector('.video-overlay-next') as HTMLDivElement | null;
        overlayVideoNextEl?.append(nextCardElement);
    }

    return {
        overlayEl: el.nextElementSibling as HTMLDivElement,
        playNextButton: el.nextElementSibling?.querySelector('.video-ovelay-play-next-button') as HTMLButtonElement,
        replayButton: el.nextElementSibling?.querySelector('.video-ovelay-replay-button') as HTMLButtonElement,
        nextCardElement,
    };
};

const VideoSlider = ({ el, props }: VideoSliderProps): void => {
    const {
        initialOpenedIndex = 0,
        label = defaultLabelValue,
    } = props;

    const infoEls: HTMLDivElement[] = Array.from(el.querySelectorAll('.video-slider-video-info'));
    const videoEls: HTMLDivElement[] = Array.from(el.querySelectorAll('.video-slider-main-video'));
    const thumbsEl: HTMLDivElement[] = Array.from(el.querySelectorAll('.video-slider-list-content'));
    const navigationEls: HTMLDivElement[] = Array.from(el.querySelectorAll('.video-slider-list-navigation-buttons'));

    const buttonCardEls: HTMLLinkElement[][] = Array.from(thumbsEl).map(thumbEl => {
        return Array.from(thumbEl.querySelectorAll('.lesson-card'));
    });

    const videoItemEls: HTMLDivElement[][] = Array.from(videoEls).map(videoEl => {
        return Array.from(videoEl.querySelectorAll('.video-item'));
    });

    // category buttons
    const dataTabTargets: HTMLElement[] = Array.from(el.querySelectorAll('[data-tab-target]'));

    // item to hide and show on active category
    const dataTabItems: HTMLElement[] = Array.from(el.querySelectorAll('[data-tab-item]'));

    if (!thumbsEl?.length || !infoEls?.length || !videoEls.length || !navigationEls.length || !buttonCardEls.length || !videoItemEls?.length) {
        return;
    }

    // items length per category
    const itemsLength = buttonCardEls.map(buttonCardEl => {
        return buttonCardEl.length;
    });

    const clearItemsLengthClass = (): void => {
        itemsLength.forEach(itemLength => {
            el.classList.remove(`items-${itemLength}`);
        });
    };

    const setItemsLengthClass = (index: number): void => {
        el.classList.add(`items-${itemsLength[index]}`);
    };

    const setActiveTab = (tabId: string, i: number): void => {
        clearItemsLengthClass();
        setItemsLengthClass(i);

        // set active on button
        dataTabTargets.forEach(tabTarget => {
            const currentTabTarget = (tabTarget as HTMLElement).dataset.tabTarget;
            if (currentTabTarget === tabId) {
                tabTarget.classList.add('is-active');
            } else {
                tabTarget.classList.remove('is-active');
            }
        });

        // hide all data-tab-item that is not active
        dataTabItems.forEach(tabItem => {
            const currentTabId = (tabItem as HTMLElement).dataset.tabItem;

            if (currentTabId === tabId) {
                tabItem.classList.remove('tab-hidden');
            } else {
                tabItem.classList.add('tab-hidden');
            }
        });
    };

    // setup players for each video
    // players is a 2D array, where the first dimension is the category index
    // and the second dimension is the video with its information index
    const players: VideoPlayer[][] = videoItemEls.map((videoItemEl, i) => {
        return videoItemEl.map((currentVideoItemEl, j) => {
            currentVideoItemEl.dataset.index = `${i.toString()},${j.toString()}`;
            const dataVideoEl: HTMLElement | null = currentVideoItemEl.querySelector('[data-video]');

            // get the next button card element, if last item get the first item
            const nextButtonCardElement = (j < videoItemEl.length - 1) ?
                buttonCardEls[i][j + 1]?.cloneNode(true) as HTMLLinkElement | null :
                buttonCardEls[i][0]?.cloneNode(true) as HTMLLinkElement | null;

            // remove active styling
            nextButtonCardElement?.classList.remove('is-active');

            // where parent to render the video overlay element
            const videoInnerEl = currentVideoItemEl.querySelector('.video-inner') as HTMLDivElement;

            // render the video overlay element and return the related elements
            const { overlayEl, playNextButton, replayButton, nextCardElement } = renderVideoOverlayEl({
                el: videoInnerEl,
                nextCardElement: nextButtonCardElement,
                label,
                isFirstItem: j === 0,
                isLastItem: j === videoItemEl.length - 1,
            });

            const playButton = currentVideoItemEl.querySelector(`.${PLAY_BTN_CLASS}`) as HTMLButtonElement;

            const overlayObject = {
                overlayEl,
                playNextButton,
                replayButton,
                nextCardElement,
            };

            const videoItemObject = {
                player: null,
                videoItemEl: currentVideoItemEl,
                buttonCardEl: buttonCardEls[i][j],
                playButton,
            };

            if (!dataVideoEl) {
                return {
                    videoSource: null,
                    ...videoItemObject,
                    ...overlayObject,
                };
            }

            const data = dataVideoEl.dataset.video;
            // if the data is not null, parse the data as a string
            const videoProps = data ? JSON.parse(data) : '';
            if (typeof props === 'string' && props !== '') {
                return {
                    videoSource: null,
                    ...videoItemObject,
                    ...overlayObject,
                };
            }

            const { url } = videoProps;

            const { videoSource } = getVideoData(url);

            return {
                videoSource,
                ...videoItemObject,
                ...overlayObject,
            };
        });
    });

    const prevButtons = navigationEls.map(navigationEl => {
        return navigationEl.querySelector('.video-slider-list-button-prev') as HTMLButtonElement | null;
    });

    const nextButtons = navigationEls.map(navigationEl => {
        return navigationEl.querySelector('.video-slider-list-button-next') as HTMLButtonElement | null;
    });

    const slidesPerViewMD = itemsLength.map(itemLength => {
        return itemLength <= 2 ? 2 : 2.23;
    });

    const slidesPerViewLG = itemsLength.map(itemLength => {
        return itemLength <= 3 ? 3 : 3.23;
    });

    const slidesPerViewXL = itemsLength.map(itemLength => {
        return itemLength <= 4 ? 4 : 4.23;
    });

    const thumbsSwipers = thumbsEl.map((thumbEl, i) => {
        return new Swiper(thumbEl, {
            watchOverflow: true,
            spaceBetween: 8,
            slidesPerView: 2.5,
            // slideToClickedSlide: true,
            watchSlidesProgress: true,
            autoHeight: false,
            modules: [Navigation, A11y],
            navigation: {
                nextEl: nextButtons[i],
                prevEl: prevButtons[i],
            },
            freeMode: true,
            direction: 'vertical',
            breakpoints: {
                768: { direction: 'horizontal', slidesPerView: slidesPerViewMD[i], spaceBetween: 8 },
                1024: { direction: 'horizontal', slidesPerView: slidesPerViewLG[i], spaceBetween: 8 },
                1440: { direction: 'horizontal', slidesPerView: slidesPerViewXL[i], spaceBetween: 8 },
            },
            init: false,
        });
    });

    const infoSliders = infoEls.map(infoEl => {
        return new Swiper(infoEl, {
            modules: [A11y, EffectFade],
            watchOverflow: true,
            effect: 'fade',
            allowTouchMove: false,
            autoHeight: true,
            init: false,
        });
    });

    const videoSliders = videoEls.map(videoEl => {
        return new Swiper(videoEl, {
            modules: [A11y, EffectFade],
            watchOverflow: true,
            effect: 'fade',
            allowTouchMove: false,
            autoHeight: true,
            init: false,
        });
    });

    const clearButtonCardActiveClass = (categoryIndex: number): void => {
        buttonCardEls[categoryIndex]?.forEach(buttonCardEl => {
            if (buttonCardEl.classList.contains('is-locked')) {
                return;
            }

            buttonCardEl.classList.remove('is-active');
        });
    };

    const setButtonCardActiveClass = (buttonCardEl: HTMLElement, categoryIndex: number): void => {
        clearButtonCardActiveClass(categoryIndex);
        buttonCardEl.classList.add('is-active');
    };


    const assignVideoPlayer = (player: VideoPlayer, videoElement: HTMLElement): void => {
        if (player.player || !videoElement) {
            return;
        }
        switch (player.videoSource) {
            case 'youtube': {
                (player.player as unknown as YoutubePlayer) = new YoutubePlayer(videoElement as HTMLIFrameElement);
                break;
            }
            case 'vimeo': {
                (player.player as unknown as VimeoPlayer) = new VimeoPlayer(videoElement as HTMLElement);
                break;
            }
            case 'upload': {
                (player.player as unknown as HTMLVideoElement) = videoElement as HTMLVideoElement;
                break;
            }
            default: {
                break;
            }
        }
    };

    const assignAllVideoPlayers = (): void => {
        videoItemEls.forEach((videoItemElements, i) => {
            videoItemElements.forEach((videoItemEl, j) => {
                const videoElement = videoItemEl.querySelector('iframe, video');

                assignVideoPlayer(players[i][j], videoElement as HTMLElement);
            });
        });
    };


    const pauseAllVideo = (): void => {
        // why this assign on action, cause the iframe is ready when the user click the play button
        assignAllVideoPlayers();

        // Pause all videos
        players.forEach(playersJ => {
            playersJ.forEach(({ player }) => {
                if (!player) {
                    return;
                }

                player.pause();
            });
        });
    };


    buttonCardEls?.forEach((buttonCardElements, i) => {
        buttonCardElements?.forEach((buttonCardEl, j) => {
            if (buttonCardEl.classList.contains('is-locked')) {
                return;
            }

            buttonCardEl.addEventListener('click', e => {
                e.preventDefault();

                infoSliders[i].slideTo(j);
                videoSliders[i].slideTo(j);
                setButtonCardActiveClass(buttonCardEl as HTMLElement, i);
                pauseAllVideo();

                if (players[i][j].overlayEl?.classList.contains('is-hidden')) {
                    players[i][j].player?.play();
                }

                if (window.innerWidth < 768) {
                    el.scrollIntoView();
                }
            });
        });
    });

    // add event listener click to category button
    dataTabTargets.forEach((tabTarget, i) => {
        const currentTabTarget = tabTarget.dataset.tabTarget ?? '';

        tabTarget.addEventListener('click', e => {
            e.preventDefault();
            pauseAllVideo();
            setActiveTab(currentTabTarget, i);
        });
    });

    // When the video is clicked for the first time it create the iframe element and trigger te video ready event when ready
    // after ready we add event listener to the video on `ended` (if vimeo) and add event listener to the overlay button
    window.addEventListener(VIDEO_READY_EVENT, e => {
        const videoData = (e as CustomEvent<VideoData>).detail;

        // make sure the video is vimeo since we only add event listener to vimeo
        if (!videoData.videoElement || videoData.videoSource !== 'vimeo') {
            return;
        }

        const parent: HTMLDivElement | null = videoData.videoElement.closest('.video-item');

        // make sure the parent is exist
        if (!parent) {
            return;
        }
        const videoIndex = parent.dataset.index?.split(',') ?? [];

        // the videoIndex needed for getting the player
        if (!videoIndex.length) {
            return;
        }

        const i = Number(videoIndex[0]);
        const j = Number(videoIndex[1]);

        const currentPlayer = players[i][j];

        assignVideoPlayer(currentPlayer, videoData.videoElement);

        // click replay button will replay the video
        currentPlayer.replayButton?.addEventListener('click', () => {
            currentPlayer.player?.play();
            currentPlayer.overlayEl?.classList.add('is-hidden');
        });

        // when click on the thumbnail inside overlay, it will play the next video
        currentPlayer.nextCardElement?.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault();
            const playerIndex = j < players[i].length - 1 ? j + 1 : 0;
            const nextPlayer = players[Number(i)][playerIndex];
            if (!nextPlayer) {
                return;
            }

            // slide to the next player
            thumbsSwipers[i].slideTo(playerIndex);

            // emulate goes to next slide
            nextPlayer.buttonCardEl?.click();

            // then plpay the video using emulate the click on the play button
            nextPlayer.playButton?.click();
        });

        (currentPlayer.player as VimeoPlayer)?.on('ended', () => {
            const vimeoPlayer = currentPlayer.player as VimeoPlayer;
            vimeoPlayer.exitPictureInPicture();
            vimeoPlayer.exitFullscreen();

            // eslint-disable-next-line no-console
            currentPlayer.overlayEl?.classList.remove('is-hidden');
        });
    });

    // Add quantity categories to parent
    el.classList.add(`categories-${dataTabTargets.length}`);

    // set first tab to active
    const initialDataTabTarget = dataTabTargets[initialOpenedIndex]?.dataset.tabTarget ?? '';
    setActiveTab(initialDataTabTarget, initialOpenedIndex);


    thumbsSwipers.forEach(thumbSwiper => {
        thumbSwiper.init();
    });

    infoSliders.forEach(infoSlider => {
        infoSlider.init();
    });

    videoSliders.forEach(videoSlider => {
        videoSlider.init();
    });
};


export default VideoSlider;
